import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LocationType } from '@rma/generic/domain/types';
import { CalloutComponent } from '@rma/generic/ui/callout';
import { provideRmaErrorStateMatcher } from '@rma/generic/ui/forms/combined';
import { SubmittingStatus } from '@rma/generic/ui/forms/submitting';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { LoadingIndicatorDirective } from '@rma/generic/ui/loading-indicator';
import { provideRecaptchaSettings, RmaRecaptchaLoaderService } from '@rma/generic/ui/rma-recaptcha';
import { TrixelComponent } from '@rma/generic/ui/trixels';
import { RecaptchaFormsModule, RecaptchaModule } from 'ng-recaptcha';
import { filter, startWith } from 'rxjs';
import type { ContactRequest, ContactType } from '../../domain/contact';
import { FindMyAgentResponseVM } from '../../domain/results';

@UntilDestroy()
@Component({
  selector: 'rma-results-contact-form',
  imports: [
    AsyncPipe,
    CalloutComponent,
    LoadingIndicatorDirective,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    ReactiveFormsModule,
    RecaptchaFormsModule,
    RecaptchaModule,
    RmaIconComponent,
    TranslocoModule,
    TrixelComponent,
  ],
  templateUrl: './contact-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideTranslocoScope('contactForm'),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    provideRmaErrorStateMatcher(),
    provideRecaptchaSettings(),
  ],
})
export class ResultsContactFormComponent implements OnChanges {
  private readonly fb = inject(NonNullableFormBuilder);
  public readonly recaptcha = inject(RmaRecaptchaLoaderService);

  @Input()
  contact!: FindMyAgentResponseVM;

  @Input()
  submittingStatus: SubmittingStatus | null = null;

  @Output()
  contactRequest = new EventEmitter<ContactRequest>();

  protected contactForm = this.createForm();

  ngOnChanges(changes: SimpleChanges): void {
    const { contact, submittingStatus } = changes;

    if (contact) {
      this.setState(this.contact);
    }

    if (submittingStatus) {
      this.resetAfterSubmission(this.submittingStatus);
    }
  }

  onSubmit() {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    const request = this.mapToContactRequest();

    this.contactRequest.emit(request);
  }

  resetAfterSubmission(status: SubmittingStatus | null) {
    if (!status || status === 'Submitting') {
      return;
    }

    if (status === 'Success') {
      this.contactForm.patchValue(
        {
          recaptcha: undefined,
        },
        { emitEvent: false },
      );
    }
  }

  private createForm() {
    return this.fb.group({
      recipientEmail: this.fb.control(''),
      recipientPhone: this.fb.control(''),
      recipientName: this.fb.control(''),
      senderEmail: this.fb.control<string | undefined>('', { validators: [Validators.email], updateOn: 'blur' }),
      senderName: this.fb.control('', { validators: [Validators.required], updateOn: 'blur' }),
      contactType: this.fb.control<ContactType>('phone', { validators: [Validators.required] }),
      senderPhone: this.fb.control<string | undefined>('', { updateOn: 'blur' }),
      message: this.fb.control<string | undefined>('', { updateOn: 'blur' }),
      isPaying: this.fb.control(false),
      recaptcha: this.fb.control('', { validators: [Validators.required] }),
      propertyType: this.fb.control(''),
      address: this.fb.control(''),
      addressId: this.fb.control(0),
      localityName: this.fb.control(''),
      locationType: this.fb.control<LocationType>('Locality'),
      locationId: this.fb.control(0),
    });
  }

  private setState(contact: FindMyAgentResponseVM) {
    this.setDefaultValue(contact);
    this.setMandatoryContactField();
    this.loadRecaptcha();
  }

  private loadRecaptcha() {
    this.contactForm.valueChanges
      .pipe(
        startWith(false),
        filter(() => this.contactForm.dirty),
        untilDestroyed(this),
      )
      .subscribe(() => this.recaptcha.load());
  }

  private setDefaultValue(contact: FindMyAgentResponseVM) {
    this.contactForm.patchValue({
      recipientEmail: contact.email,
      recipientPhone: contact.phone,
      recipientName: contact.shortName ?? contact.name,
      propertyType: contact.propertyType,
      isPaying: contact.branding.hasBranding,
      addressId: contact.addressId,
      address: contact.address,
      locationId: contact.locationId,
      localityName: contact.localityName,
      locationType: contact.locationType,
    });
  }

  private setMandatoryContactField() {
    this.contactForm.controls.contactType.valueChanges
      .pipe(startWith(this.contactForm.controls.contactType.value), untilDestroyed(this))
      .subscribe((contactType) => {
        if (contactType === 'email') {
          this.contactForm.controls.senderEmail.setValidators([Validators.required, Validators.email]);
          this.contactForm.controls.senderPhone.clearValidators();
        } else {
          this.contactForm.controls.senderPhone.setValidators([Validators.required]);
          this.contactForm.controls.senderEmail.clearValidators();
        }

        this.contactForm.controls.senderPhone.updateValueAndValidity();
        this.contactForm.controls.senderEmail.updateValueAndValidity();
      });
  }

  private mapToContactRequest(): ContactRequest {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { contactType, recaptcha, ...contactRequest } = this.contactForm.getRawValue();

    return { ...contactRequest, recaptchaResponse: recaptcha };
  }
}
